import {
  Link,
  useMatchRoute,
  useNavigate,
  useParams,
  useRouteContext,
} from "@tanstack/react-router";
import type {
  AnyRouter,
  LinkComponentProps,
  NavigateOptions,
} from "@tanstack/react-router";
import type { ReactElement } from "react";

export type ScopedNavigate<TRouter extends AnyRouter> = <
  const TTo extends string | undefined = undefined,
  const TFrom extends string = string,
  const TMaskFrom extends string = TFrom,
  const TMaskTo extends string = "",
>(
  options: NavigateOptions<TRouter, TFrom, TTo, TMaskFrom, TMaskTo>,
) => Promise<void>;

export type ScopedLinkComponent<TRouter extends AnyRouter> = <
  const TTo extends string | undefined = undefined,
  const TFrom extends string = string,
  const TMaskFrom extends string = TFrom,
  const TMaskTo extends string = "",
>(
  props: LinkComponentProps<"a", TRouter, TFrom, TTo, TMaskFrom, TMaskTo>,
) => ReactElement;

export type ScopedUseParams<TRouter extends AnyRouter> = <
  const TFrom extends string | undefined = undefined,
  TStrict extends boolean = true,
  TThrow extends boolean = true,
  TSelected = unknown,
  TStructuralSharing extends boolean = boolean,
>(
  ...args: Parameters<
    typeof useParams<
      TRouter,
      TFrom,
      TStrict,
      TThrow,
      TSelected,
      TStructuralSharing
    >
  >
) => ReturnType<
  typeof useParams<
    TRouter,
    TFrom,
    TStrict,
    TThrow,
    TSelected,
    TStructuralSharing
  >
>;

export type ScopedUseRouteContext<TRouter extends AnyRouter> = <
  const TFrom extends string | undefined = undefined,
  TStrict extends boolean = true,
  TSelected = unknown,
>(
  ...args: Parameters<
    typeof useRouteContext<TRouter, TFrom, TStrict, TSelected>
  >
) => ReturnType<typeof useRouteContext<TRouter, TFrom, TStrict, TSelected>>;

/**
 * Builds router-scoped hooks and a `Link` component for an isolated
 * (e.g. in-memory) router whose type is NOT the global `RegisteredRouter`.
 *
 * Each isolated router calls this once with its own `TRouter` type and
 * re-exports the returned members.
 */
export const createScopedRouterHelpers = <TRouter extends AnyRouter>() => {
  const useScopedNavigate = () => useNavigate() as ScopedNavigate<TRouter>;

  const useScopedMatchRoute = useMatchRoute<TRouter>;

  const useScopedParams = useParams as ScopedUseParams<TRouter>;

  const useScopedRouteContext =
    useRouteContext as ScopedUseRouteContext<TRouter>;

  const ScopedLink = Link as ScopedLinkComponent<TRouter>;

  return {
    useScopedNavigate,
    useScopedMatchRoute,
    useScopedParams,
    useScopedRouteContext,
    ScopedLink,
  };
};
